import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceTemplate,
  IExperienceTemplateResource
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import {
  Resource,
  ResourceEditDialog,
  tplToDefaultTitle
} from '../../../../components/Resource';
import { useTranslation } from '../../../../i18next';
import { ONE_DAY } from '../../../../services/time';

const HeadingContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(0.5)
}));

const Heading: React.FC = ({ children }) => {
  return (
    <HeadingContainer>
      <Typography component="h6" variant="caption">
        {children}
      </Typography>
    </HeadingContainer>
  );
};

export const TemplateMetaGrid = ({
  d,
  showTitle,
  showAdminTitle,
  resourceTooltipMode = 'future'
}: {
  d: Doc<IExperienceTemplate>;
  showTitle?: boolean;
  showAdminTitle?: boolean;
  resourceTooltipMode: 'inProgress' | 'future';
}) => {
  const { t } = useTranslation();
  const [
    editableResource,
    setEditableResource
  ] = useState<IExperienceTemplateResource | null>(null);
  return (
    <Grid container spacing={3}>
      {showTitle && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Heading>{t('templateBuilder.basic.expTitle')}</Heading>
          <Typography variant="body1">{d.data.title}</Typography>
        </Grid>
      )}
      {showAdminTitle && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Heading>{t('templateBuilder.basic.expAdminTitle')}</Heading>
          <Typography variant="body1">{d.data.adminTitle}</Typography>
        </Grid>
      )}
      {d.data.notificationSender.name && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Heading>{t('templateBuilder.basic.senderName')}</Heading>
          <Typography variant="body1">
            {d.data.notificationSender.name}
          </Typography>
        </Grid>
      )}
      {d.data.notificationSender.replyTo && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Heading>{t('templateBuilder.basic.replyTo')}</Heading>
          <Typography variant="body1">
            {d.data.notificationSender.replyTo}
          </Typography>
        </Grid>
      )}
      {d.data.sso && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Heading>{t('templateBuilder.basic.requireSsoShort')}</Heading>
          <Typography variant="body1">
            {d.data.sso.type === 'ALWAYS'
              ? t('templateBuilder.basic.ssoOptions.always')
              : d.data.sso.offset === 0
              ? t('templateBuilder.basic.ssoOptions.onFirstDayAtWork')
              : t(
                  'templateBuilder.basic.ssoOptions.afterFirstDayAtWorkWithCount',
                  { count: Math.floor(d.data.sso.offset / ONE_DAY) }
                )}
          </Typography>
        </Grid>
      )}
      {!!d.data.resources.length && (
        <Grid item>
          <Heading>{t('resources.heading')}</Heading>
          {d.data.resources.map((r) => (
            <Resource
              key={r.resourceId}
              d={r}
              defaultTitle={tplToDefaultTitle(d)}
              tooltipType={resourceTooltipMode}
              onOpenSettings={() => setEditableResource(r)}
            />
          ))}
        </Grid>
      )}

      <ResourceEditDialog
        d={d}
        resource={editableResource}
        onClose={() => setEditableResource(null)}
      />
    </Grid>
  );
};

